import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { gbp, makeFilters, toParams } from "@/lib/format";
import { FilterBar } from "@/components/FilterBar";
import { PageHeader } from "@/components/PageHeader";
import { OrdersTable } from "@/components/OrdersTable";
import { OrderDetailSheet } from "@/components/OrderDetailSheet";
import { UploadZone } from "@/components/UploadZone";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Approvals() {
  const { meta, refreshMeta } = useAuth();
  const [filters, setFilters] = useState(makeFilters("all"));
  const [tab, setTab] = useState("pending_approval");
  const [data, setData] = useState({ items: [], total: 0, total_value: 0 });
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/orders", { params: { ...toParams(filters), status: tab, source: "web_upload", limit: 200 } }).then((r) => setData(r.data));
  useEffect(() => { load(); setSelected([]); }, [filters, tab]); // eslint-disable-line
  const refresh = () => { load(); refreshMeta(); };

  const bulk = async () => {
    try {
      const { data: r } = await api.post("/orders/bulk-approve", { ids: selected });
      toast.success(`${r.approved.length} order(s) approved`);
      setSelected([]);
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Incoming web orders" title="Order approval queue">
        {meta.can_approve && tab === "pending_approval" && (
          <Button disabled={!selected.length} onClick={bulk} className="bg-[#00573F] hover:bg-[#003D2C]" data-testid="bulk-approve-btn">
            <CheckCheck className="mr-1 h-4 w-4" />Approve selected ({selected.length})
          </Button>)}
      </PageHeader>
      {!meta.can_approve && (
        <div className="rounded-lg border border-[#C5A059]/40 bg-[#C5A059]/10 p-4 text-sm" data-testid="approval-readonly-note">
          As a Concession Manager you can upload order files and track them here. Approval is carried out by your Store Manager, Regional Manager or Head Office.
        </div>)}
      <UploadZone onUploaded={refresh} />
      <FilterBar filters={filters} setFilters={setFilters} defaultPreset="all" show={{ search: true }} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="pending_approval" data-testid="approval-tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="approved" data-testid="approval-tab-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="approval-tab-rejected">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="text-sm text-slate-500" data-testid="approval-queue-summary">{data.total} orders · <b className="text-slate-900">{gbp(data.total_value)}</b></div>
      </div>
      <OrdersTable items={data.items} onOpen={setOpen} selectable={meta.can_approve && tab === "pending_approval"} selected={selected} setSelected={setSelected} />
      <OrderDetailSheet orderId={open} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}
